import React from "react";
import styled from "styled-components";
import InputMask from "react-input-mask";
import StandardButton from "../../../components/Buttons/StandardButton";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { AxiosError } from "axios";
import {
  GetSettingResponse,
  getSettings,
  putSmsReminderTimeOffset,
} from "../../../requests/settings";
import { useSnackbar } from "notistack";

const SectionContainer = styled.div`
  padding: ${({ theme }) => theme.gap[1]};
  background-color: ${({ theme }) => theme.colors.base.white};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  border-radius: ${({ theme }) => theme.shape.borderRadius};
  box-sizing: border-box;
`;

const LeftSide = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
`;

const Input = styled.input<{ errored?: boolean }>`
  border-radius: ${({ theme }) => theme.shape.borderRadius};
  border: 1px solid
    ${({ theme, errored }) =>
      errored ? theme.colors.deepRed : theme.colors.greyish};
  padding: ${({ theme }) => theme.gap[1]};
  width: 80px;
  box-sizing: border-box;
  font-family: monospace;
  margin: 0 ${({ theme }) => theme.gap[1]};
`;

const Title = styled.span`
  font-size: ${({ theme }) => theme.fonts.size.default};
  color: ${({ theme }) => theme.colors.greyishBrown};
  display: flex;
  align-items: center;
`;

interface FormInputs {
  offset: string;
}

function convertToHHmm(minutes: number) {
  const hours = minutes / 60;
  const roundedHours = Math.floor(hours);
  const min = (hours - roundedHours) * 60;
  const roundedMinutes = Math.round(min);
  return `${roundedHours
    .toString()
    .padStart(2, "0")}:${roundedMinutes.toString().padStart(2, "0")}`;
}

function convertToMinutes(HHmm: string) {
  const [HH, mm] = HHmm.split(":");
  return Number(HH) * 60 + Number(mm);
}

function SmsTimeOffsetForm() {
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, refetch } = useQuery<GetSettingResponse, AxiosError>(
    "tenant-settings-sms-reminder",
    getSettings,
    {
      refetchOnWindowFocus: false,
      onError: (err) => {
        enqueueSnackbar(
          err.response && err.response.data.message
            ? err.response.data.message
            : `Unable to get SMS reminder time offset`,
          { variant: "error" }
        );
      },
    }
  );

  const { mutate } = useMutation<unknown, AxiosError, number>(
    (offset) => putSmsReminderTimeOffset(offset),
    {
      onError: (err) => {
        enqueueSnackbar(
          err.response && err.response.data.message
            ? err.response.data.message
            : `Unable to set new offset`,
          { variant: "error" }
        );
      },
      onSuccess: async () => {
        await refetch();
        reset();
        enqueueSnackbar(`New offset saved`, {
          variant: "success",
        });
      },
    }
  );

  const { register, handleSubmit, errors, reset } = useForm<FormInputs>();

  function onSubmit(v: FormInputs) {
    mutate(convertToMinutes(v.offset));
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SectionContainer>
        <LeftSide>
          <Title>SMS reminder offset (HH:mm)</Title>

          {!isLoading && (
            <InputMask
              autoComplete="off"
              id="sms-reminder-time-offset"
              name="offset"
              placeholder="HH:mm"
              mask="99:99"
              defaultValue={data && convertToHHmm(data?.smsReminderOffset)}
            >
              {(inputProps: any) => (
                <Input
                  {...inputProps}
                  errored={errors.offset}
                  ref={register({
                    pattern: /^(\d)+:[0-5][0-9]$/,
                  })}
                />
              )}
            </InputMask>
          )}
        </LeftSide>
        <StandardButton type="submit" title="Save" accent />
      </SectionContainer>
    </form>
  );
}

export default SmsTimeOffsetForm;
